import { useEffect, useState, useMemo } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import {
  Sparkles, Save, CheckCircle2, Loader, FileText, AlertCircle, Info,
  ArrowRight, ArrowLeft, MessageCircleQuestion,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import EtapaFinalPaywall from '../caja/EtapaFinalPaywall'

// ── Mi Modelo de Negocio · Diálogo iterativo ────────────────────────────
// Flujo:
//   Step 'initial'    → 7 preguntas guía sobre el modelo de negocio
//                       Botón "Evaluar y continuar" → llama etapa0_evaluate
//   Step 'followups'  → IA detectó gaps, presenta 3-5 preguntas específicas
//                       Botón "Continuar" → si pct >= 80 genera informe final;
//                       si no, otra ronda de followups (hasta ronda 3)
//   Step 'final'      → muestra informe final + (paywall lo provee otro
//                       componente externo cuando esté listo)
//
// La barra de completitud arriba refleja project_ficha.etapa0_completeness_pct
// La info de qué ronda va se guarda en project_ficha.etapa0_round
// ────────────────────────────────────────────────────────────────────────

const PROMPTS = [
  {
    key: 'bm_descripcion_libre',
    label: 'Mi modelo de negocio en mis propias palabras',
    helper: 'Escribe libremente lo que TÚ crees que es tu modelo de negocio hoy. Como si se lo explicaras a un amigo.',
    placeholder: 'Mi empresa nació hace X años cuando detectamos que… Hoy vendemos… El cliente principal es… Lo que nos diferencia es…',
    rows: 6, primary: true,
  },
  { key: 'bm_que_vendes',         label: '¿Qué vendes exactamente?',          helper: 'Productos, servicios, suscripciones. Si tienes varias líneas, lístalas.', placeholder: 'Ej: Concentrados nutricionales para porcicultura tecnificada (premium y económica) + asistencia técnica.', rows: 3 },
  { key: 'bm_a_quien',            label: '¿A quién le vendes?',                helper: 'Tipo de empresa, tamaño, industria, región. Si es B2C, perfil del consumidor.',                                placeholder: 'Ej: Porcicultores tecnificados de >500 madres en Valle, Antioquia, Eje Cafetero. 80% del volumen viene de 20 clientes.',                                                rows: 3 },
  { key: 'bm_como_entregas',      label: '¿Cómo lo entregas?',                 helper: 'Canales (directo, distribuidor, e-commerce), logística, contacto comercial.',                                  placeholder: 'Ej: Venta directa con asesores técnicos + distribuidor en costa norte. Despacho desde planta Cali.',                                                                   rows: 3 },
  { key: 'bm_como_ganas_dinero',  label: '¿Cómo ganas dinero?',                helper: 'Estructura de ingresos y márgenes. ¿De dónde viene la utilidad? ¿Qué línea/canal es más rentable?',           placeholder: 'Ej: Margen bruto ~22% premium, ~14% económica. Premium = 35% volumen pero 60% utilidad.',                                                                              rows: 3 },
  { key: 'bm_que_te_hace_unico',  label: '¿Qué te hace distinto de tus competidores?', helper: 'Lo que tú crees que el cliente valora de ti y no encuentra en otros. Si la respuesta es "el precio", piénsalo de nuevo.', placeholder: 'Ej: Únicos con planta certificada GMP+ en la región sur; equipo técnico hace seguimiento mensual zootécnico.', rows: 3 },
  { key: 'bm_supuestos',          label: 'Supuestos que tengo pero no he validado', helper: 'Cosas en las que CREES pero no has confirmado con datos. La metodología NVC va a poner cada supuesto a prueba.',  placeholder: 'Ej: Creo que mi precio es competitivo. Creo que los clientes valoran asistencia técnica más que producto. Creo que el mercado de aves es más grande que el de porcinos.', rows: 3 },
]

export default function MiModeloNegocio({ consultancyId }) {
  const { isReadOnly, isCaja, profile } = useAuth()
  // Distinguir flujo Caja (iterativo + paywall) del flujo NVC consultor
  // (un solo paso: preguntas → informe). El criterio es el rol del usuario:
  // un caja_user ve el flujo Caja; un admin/consultant ve el flujo NVC.
  const isNVCMode = !isCaja
  const [ficha, setFicha]     = useState(null)
  const [companyName, setCompanyName] = useState('')
  const [form, setForm]       = useState({})
  const [saving, setSaving]   = useState(false)
  const [saved, setSaved]     = useState(false)
  const [busy, setBusy]       = useState(false)   // procesando IA
  const [busyElapsed, setBusyElapsed] = useState(0) // segundos transcurridos
  const [error, setError]     = useState('')
  const [followupAnswers, setFollowupAnswers] = useState({})

  // Contador de segundos mientras la IA procesa
  useEffect(() => {
    if (!busy) { setBusyElapsed(0); return }
    const start = Date.now()
    const interval = setInterval(() => {
      setBusyElapsed(Math.floor((Date.now() - start) / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [busy])

  useEffect(() => { if (consultancyId) load() }, [consultancyId])

  async function load() {
    const [{ data: f }, { data: c }] = await Promise.all([
      supabase.from('project_ficha').select('*').eq('consultancy_id', consultancyId).maybeSingle(),
      supabase.from('consultancies').select('client_company, name').eq('id', consultancyId).maybeSingle(),
    ])
    setCompanyName(c?.client_company ?? c?.name ?? '')
    if (f) { setFicha(f); setForm(f); setFollowupAnswers(f.etapa0_followup_answers ?? {}) }
    else {
      const { data: stub } = await supabase
        .from('project_ficha').insert({ consultancy_id: consultancyId, equipo_nvc: [] }).select().single()
      if (stub) { setFicha(stub); setForm(stub) }
    }
  }

  // ── Step derivation ────────────────────────────────────────────────────
  const step = useMemo(() => {
    if (!ficha) return 'loading'
    if (ficha.etapa0_final_report) return 'final'
    const fq = Array.isArray(ficha.etapa0_followup_questions) ? ficha.etapa0_followup_questions : []
    if (fq.length > 0) return 'followups'
    return 'initial'
  }, [ficha])

  const completeness  = ficha?.etapa0_completeness_pct ?? 0
  const currentRound  = ficha?.etapa0_round ?? 1

  // ── Save initial form ──────────────────────────────────────────────────
  function setField(key, val) { setForm(f => ({ ...f, [key]: val })) }
  async function handleSave() {
    setSaving(true)
    const payload = Object.fromEntries(PROMPTS.map(p => [p.key, form[p.key] ?? null]))
    payload.updated_at = new Date().toISOString()
    const { data } = await supabase
      .from('project_ficha').update(payload).eq('consultancy_id', consultancyId).select().single()
    if (data) { setFicha(data); setForm(data) }
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2500)
  }

  // ── Llamar a etapa0_evaluate / etapa0_final_report ────────────────────
  async function callAI(reportType, body) {
    const { data: { session } } = await supabase.auth.getSession()

    // Timeout de 5 min — Claude Sonnet típicamente responde en 30-90s pero
    // damos buffer generoso para que nunca aborte una request que iba bien.
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 300_000)

    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-report`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ reportType, consultancy_id: consultancyId, ...body }),
          signal: controller.signal,
        }
      )
      clearTimeout(timeoutId)
      const text = await res.text()
      let data
      try { data = JSON.parse(text) } catch { data = { raw: text } }
      if (!res.ok) {
        console.error('[ai-report] error response', { status: res.status, body: data })
        throw new Error(data?.error ?? `Error ${res.status}: ${text.slice(0, 200)}`)
      }
      return data
    } catch (err) {
      clearTimeout(timeoutId)
      if (err.name === 'AbortError') {
        throw new Error('La IA tardó más de 5 minutos. Refresca la página e intenta de nuevo.')
      }
      throw err
    }
  }

  // ── Evaluar modelo + obtener follow-ups o pasar al informe final ──────
  async function evaluateAndContinue() {
    setBusy(true); setError('')
    try {
      await handleSave()  // guardar primero
      const modelo = Object.fromEntries(PROMPTS.map(p => [p.key, form[p.key] ?? null]))
      const fq = Array.isArray(ficha?.etapa0_followup_questions) ? ficha.etapa0_followup_questions : []
      const eva = await callAI('etapa0_evaluate', {
        modelo_negocio: modelo,
        followup_questions: fq,
        followup_answers: followupAnswers,
        round: currentRound,
      })
      const { completeness_pct, has_enough_info, followup_questions = [] } = eva.evaluation ?? {}

      // Si ya hay suficiente info o ya llegamos a ronda 3 → generar informe final
      if (has_enough_info || currentRound >= 3 || followup_questions.length === 0) {
        await generateFinalReport(modelo, fq, completeness_pct ?? completeness)
        return
      }

      // Si no, guardar las nuevas preguntas y avanzar de ronda
      const annotated = followup_questions.map((q, i) => ({
        round: currentRound + 1,
        key: q.key || `r${currentRound + 1}_q${i + 1}`,
        question: q.question || String(q),
      }))
      // Las preguntas se acumulan a través de rondas (no se reemplazan)
      const allFq = [...fq, ...annotated]

      const { data: updated } = await supabase
        .from('project_ficha')
        .update({
          etapa0_round: currentRound + 1,
          etapa0_followup_questions: allFq,
          etapa0_completeness_pct: completeness_pct ?? completeness,
          updated_at: new Date().toISOString(),
        })
        .eq('consultancy_id', consultancyId)
        .select().single()
      if (updated) setFicha(updated)
    } catch (err) {
      setError(err.message ?? 'No se pudo procesar')
    } finally {
      setBusy(false)
    }
  }

  // ── Guardar respuestas de followups (sin avanzar) ──────────────────────
  async function saveFollowupAnswers() {
    setSaving(true)
    const { data } = await supabase
      .from('project_ficha')
      .update({ etapa0_followup_answers: followupAnswers, updated_at: new Date().toISOString() })
      .eq('consultancy_id', consultancyId).select().single()
    if (data) setFicha(data)
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2500)
  }

  // ── Generar informe final ─────────────────────────────────────────────
  async function generateFinalReport(modeloMaybe, fqMaybe, pctMaybe) {
    setBusy(true); setError('')
    try {
      const modelo = modeloMaybe ?? Object.fromEntries(PROMPTS.map(p => [p.key, form[p.key] ?? null]))
      const fq     = fqMaybe ?? (Array.isArray(ficha?.etapa0_followup_questions) ? ficha.etapa0_followup_questions : [])
      const out = await callAI('etapa0_final_report', {
        modelo_negocio:      modelo,
        followup_questions:  fq,
        followup_answers:    followupAnswers,
        round:               currentRound,
      })
      const reportText = out.report ?? out.content ?? ''
      if (!reportText) throw new Error('La IA no devolvió contenido')
      const { data: updated } = await supabase
        .from('project_ficha')
        .update({
          etapa0_final_report: reportText,
          etapa0_final_report_generated_at: new Date().toISOString(),
          etapa0_completed_at: new Date().toISOString(),
          etapa0_completeness_pct: pctMaybe ?? 100,
          updated_at: new Date().toISOString(),
        })
        .eq('consultancy_id', consultancyId).select().single()
      if (updated) setFicha(updated)
    } catch (err) {
      setError(err.message ?? 'No se pudo generar el informe final')
    } finally {
      setBusy(false)
    }
  }

  // ── NVC mode: generar informe directo (sin iterar followups, sin paywall) ──
  // El admin/consultor llena las 7 preguntas y la IA genera un informe único.
  // Se guarda en project_ficha (para mostrar inline) Y en ai_reports (para
  // que aparezca también en la pestaña "Informe IA" del nivel etapa).
  async function generateNVCReport() {
    setBusy(true); setError('')
    try {
      await handleSave()
      const modelo = Object.fromEntries(PROMPTS.map(p => [p.key, form[p.key] ?? null]))
      const out = await callAI('etapa0_final_report', {
        modelo_negocio:     modelo,
        followup_questions: [],
        followup_answers:   {},
        round:              1,
      })
      const reportText = out.report ?? out.content ?? ''
      if (!reportText) throw new Error('La IA no devolvió contenido')

      // Persist en project_ficha (driver del step='final')
      const { data: updated } = await supabase
        .from('project_ficha')
        .update({
          etapa0_final_report: reportText,
          etapa0_final_report_generated_at: new Date().toISOString(),
          etapa0_completed_at: new Date().toISOString(),
          etapa0_completeness_pct: 100,
          updated_at: new Date().toISOString(),
        })
        .eq('consultancy_id', consultancyId).select().single()
      if (updated) setFicha(updated)

      // Persist en ai_reports para que aparezca en "Informe IA" tab
      try {
        await supabase.from('ai_reports').insert({
          consultancy_id: consultancyId,
          entity_type: 'phase',
          entity_id: null,
          report_type: 'modelo_negocio',
          content: reportText,
          generated_by: profile?.id ?? null,
        })
      } catch (logErr) {
        console.warn('ai_reports insert failed (no bloqueante):', logErr)
      }
    } catch (err) {
      setError(err.message ?? 'No se pudo generar el informe')
    } finally {
      setBusy(false)
    }
  }

  // ── Resetear / reiniciar diálogo (botón dev/debug) ────────────────────
  async function resetDialogue() {
    if (!confirm('¿Reiniciar el diálogo de Etapa 0? Se borran preguntas/respuestas y el informe final.')) return
    setBusy(true)
    const { data } = await supabase
      .from('project_ficha')
      .update({
        etapa0_round: 1,
        etapa0_followup_questions: [],
        etapa0_followup_answers: {},
        etapa0_completeness_pct: 0,
        etapa0_final_report: null,
        etapa0_final_report_generated_at: null,
      })
      .eq('consultancy_id', consultancyId).select().single()
    if (data) { setFicha(data); setFollowupAnswers({}) }
    setBusy(false)
  }

  // ── Render ─────────────────────────────────────────────────────────────
  if (!ficha) return (
    <div className="flex items-center justify-center py-10">
      <Loader size={20} className="animate-spin text-brand-500" />
    </div>
  )

  return (
    <div className="space-y-6">
      {isNVCMode
        ? <NVCHeaderHero hasReport={!!ficha?.etapa0_final_report} />
        : <HeaderHero step={step} round={currentRound} />
      }
      {!isNVCMode && <CompletenessBar pct={completeness} step={step} />}
      {error && (
        <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {/* En modo NVC siempre se muestra el formulario, incluso después de
          generar el informe (para regenerarlo si se quiere actualizar). */}
      {(step === 'initial' || isNVCMode) && (
        <InitialForm
          form={form}
          setField={setField}
          onSave={handleSave}
          onContinue={isNVCMode ? generateNVCReport : evaluateAndContinue}
          saving={saving}
          saved={saved}
          busy={busy}
          busyElapsed={busyElapsed}
          readOnly={isReadOnly}
          isNVCMode={isNVCMode}
          hasExistingReport={!!ficha?.etapa0_final_report}
        />
      )}

      {step === 'followups' && !isNVCMode && (
        <FollowupsForm
          questions={ficha.etapa0_followup_questions ?? []}
          answers={followupAnswers}
          setAnswers={setFollowupAnswers}
          onSave={saveFollowupAnswers}
          onContinue={evaluateAndContinue}
          saving={saving}
          saved={saved}
          busy={busy}
          round={currentRound}
        />
      )}

      {step === 'final' && (
        <>
          <FinalReportView report={ficha.etapa0_final_report} ficha={ficha} />
          {/* Paywall solo para flujo Caja — en modo NVC no aplica */}
          {!isNVCMode && (
            <EtapaFinalPaywall
              report={ficha.etapa0_final_report}
              ficha={ficha}
              consultancyId={consultancyId}
              companyName={companyName}
            />
          )}
        </>
      )}

      {/* Botón reset escondido — útil para iterar mientras testeas. Quitable. */}
      {step !== 'initial' && (
        <details className="text-xs text-gray-400">
          <summary className="cursor-pointer hover:text-gray-600">⚙️ Opciones avanzadas</summary>
          <button onClick={resetDialogue} disabled={busy}
            className="mt-2 px-3 py-1.5 rounded border border-gray-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600">
            Reiniciar diálogo desde cero
          </button>
        </details>
      )}
    </div>
  )
}

// ── Header hero para NVC consultor (más simple, sin "Paso 1 de 3") ──────
function NVCHeaderHero({ hasReport }) {
  return (
    <div className="rounded-xl bg-gradient-to-r from-brand-700 to-brand-900 px-5 py-4 text-white">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
          <Sparkles size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] text-brand-200 font-bold">Etapa 0 · Modelo de Negocio</p>
          <h2 className="font-semibold text-base mt-0.5">
            {hasReport ? 'Informe del Modelo de Negocio' : 'Captura del Modelo de Negocio'}
          </h2>
          <p className="text-brand-100 text-xs mt-1 leading-relaxed">
            Llena las 7 preguntas y la IA va a generar un informe del entendimiento del modelo
            de negocio del cliente. Puedes regenerarlo cuando actualices la información.
          </p>
        </div>
      </div>
    </div>
  )
}

// ── Header hero ───────────────────────────────────────────────────────────
function HeaderHero({ step, round }) {
  const label =
    step === 'initial'   ? 'Paso 1 de 3 · Cuéntame tu modelo' :
    step === 'followups' ? `Paso 2 de 3 · Preguntas de seguimiento (ronda ${round})` :
    step === 'final'     ? 'Paso 3 de 3 · Tu diagnóstico final' :
                            'Cargando…'
  return (
    <div className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-700 px-5 py-4 text-white">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
          <Sparkles size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] text-indigo-200 font-bold">Etapa 0 · Diálogo</p>
          <h2 className="font-semibold text-base mt-0.5">{label}</h2>
          <p className="text-indigo-100 text-xs mt-1 leading-relaxed">
            La IA te va a hacer preguntas hasta entender bien tu negocio. Después
            genera un diagnóstico final que conecta cada hallazgo con la metodología NVC.
          </p>
        </div>
      </div>
    </div>
  )
}

// ── Barra de completitud ─────────────────────────────────────────────────
function CompletenessBar({ pct, step }) {
  const tone =
    pct >= 80 ? 'bg-emerald-500' :
    pct >= 50 ? 'bg-accent-500'  :
    pct >= 25 ? 'bg-amber-500'   :
                 'bg-gray-300'
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Completitud del diagnóstico</span>
          <span className="text-xs tabular-nums text-gray-700 font-bold">{pct}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full ${tone} transition-all duration-500`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      {step !== 'final' && (
        <span className="text-[11px] text-gray-400">
          La barra avanza después de cada evaluación de la IA
        </span>
      )}
    </div>
  )
}

// ── Step: formulario inicial (7 preguntas guía) ───────────────────────────
function InitialForm({ form, setField, onSave, onContinue, saving, saved, busy, busyElapsed = 0, readOnly, isNVCMode = false, hasExistingReport = false }) {
  const filledCount = PROMPTS.filter(p => (form[p.key] ?? '').trim().length > 20).length
  const canContinue = filledCount >= 4

  // Mensaje progresivo según tiempo transcurrido
  const busyMessage = (() => {
    if (busyElapsed < 10) return 'La IA está leyendo el modelo…'
    if (busyElapsed < 30) return `Analizando respuestas… (${busyElapsed}s)`
    if (busyElapsed < 60) return `Estructurando el diagnóstico… (${busyElapsed}s)`
    if (busyElapsed < 90) return `Redactando el informe ejecutivo… (${busyElapsed}s)`
    if (busyElapsed < 120) return `Ya casi… (${busyElapsed}s — los informes complejos toman 1-2 min)`
    return `⚠️ Tomando más de lo normal (${busyElapsed}s) — revisa la consola (F12)`
  })()
  return (
    <>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-gray-500">
          Has respondido <span className="font-bold text-gray-800">{filledCount}</span> de {PROMPTS.length} preguntas con detalle.
        </div>
        {!readOnly && (
          <button onClick={onSave} disabled={saving}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              saved ? 'bg-emerald-500 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            } disabled:opacity-60`}>
            {saving ? <><Loader size={12} className="animate-spin" />Guardando…</>
              : saved ? <><CheckCircle2 size={12} />¡Guardado!</>
              : <><Save size={12} />Guardar borrador</>}
          </button>
        )}
      </div>

      <div className="space-y-5">
        {PROMPTS.map(p => (
          <div key={p.key} className={`card ${p.primary ? 'border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white' : ''}`}>
            <label className="block">
              <div className="flex items-start gap-2 mb-2">
                <span className="text-sm font-semibold text-gray-800 leading-snug">{p.label}</span>
                {p.primary && (
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-indigo-600 text-white px-2 py-0.5 rounded-full whitespace-nowrap">
                    Principal
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-2.5 leading-relaxed">{p.helper}</p>
              <textarea
                value={form[p.key] ?? ''}
                onChange={e => setField(p.key, e.target.value)}
                placeholder={p.placeholder}
                rows={p.rows}
                disabled={readOnly}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 resize-y bg-white disabled:bg-gray-50 disabled:text-gray-500"
              />
            </label>
          </div>
        ))}
      </div>

      {/* CTA continuar */}
      {!readOnly && (
        <div className={`card border-2 ${isNVCMode ? 'border-brand-200 bg-gradient-to-br from-brand-50/60 to-white' : 'border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-violet-50/40'}`}>
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl ${isNVCMode ? 'bg-brand-700' : 'bg-indigo-600'} flex items-center justify-center flex-shrink-0`}>
              {isNVCMode ? <FileText size={18} className="text-white" /> : <MessageCircleQuestion size={18} className="text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-gray-900">
                {isNVCMode
                  ? (hasExistingReport ? 'Regenerar informe' : 'Generar informe del modelo de negocio')
                  : 'Continuar al siguiente paso'}
              </h3>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                {isNVCMode
                  ? 'La IA va a leer las respuestas y producir un informe ejecutivo del modelo de negocio del cliente, listo para usarlo como base de la consultoría.'
                  : 'La IA va a leer tus respuestas y decidir si tiene suficiente info o necesita hacerte preguntas adicionales antes del diagnóstico final.'}
              </p>
              {!canContinue && (
                <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-100/50 rounded-lg px-3 py-2">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  Necesitas al menos 4 respuestas con detalle (más de 20 caracteres cada una) para {isNVCMode ? 'generar el informe' : 'continuar'}.
                </div>
              )}
              <button
                onClick={onContinue}
                disabled={!canContinue || busy}
                className={`mt-4 flex items-center gap-2 ${isNVCMode
                  ? 'bg-gradient-to-r from-brand-700 to-brand-900 hover:from-brand-800 hover:to-brand-950'
                  : 'bg-gradient-to-r from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800'
                } disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all shadow-sm`}
              >
                {busy
                  ? <><Loader size={14} className="animate-spin" />{busyMessage}</>
                  : isNVCMode
                    ? <><Sparkles size={14} />{hasExistingReport ? 'Regenerar informe' : 'Generar informe'}</>
                    : <><ArrowRight size={14} />Evaluar y continuar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ── Step: preguntas de follow-up (rondas 2-3) ────────────────────────────
function FollowupsForm({ questions, answers, setAnswers, onSave, onContinue, saving, saved, busy, round }) {
  // Mostrar SOLO las preguntas de la ronda actual (no acumuladas)
  const currentQuestions = questions.filter(q => q.round === round)
  const allKeys = currentQuestions.map(q => q.key)
  const filledCount = allKeys.filter(k => (answers[k] ?? '').trim().length > 5).length
  const canContinue = filledCount === currentQuestions.length && currentQuestions.length > 0

  function setAnswer(key, val) { setAnswers(a => ({ ...a, [key]: val })) }

  return (
    <>
      <div className="card border-2 border-violet-200 bg-violet-50/40">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-violet-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-violet-900 leading-relaxed">
            La IA leyó tu modelo y detectó <strong>{currentQuestions.length} cosas que necesita aclarar</strong>{' '}
            para darte un diagnóstico sólido. Respóndelas (cortito está bien) y avanzamos al cierre.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {currentQuestions.map((q, i) => (
          <div key={q.key} className="card border border-gray-200">
            <label className="block">
              <div className="flex items-start gap-2 mb-2">
                <span className="text-xs font-bold text-violet-600 bg-violet-100 px-2 py-0.5 rounded">
                  {i + 1}
                </span>
                <span className="text-sm font-semibold text-gray-800 leading-snug flex-1">
                  {q.question}
                </span>
              </div>
              <textarea
                value={answers[q.key] ?? ''}
                onChange={e => setAnswer(q.key, e.target.value)}
                rows={3}
                placeholder="Tu respuesta…"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 resize-y bg-white"
              />
            </label>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
        <button onClick={onSave} disabled={saving || busy}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${
            saved ? 'bg-emerald-500 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
          } disabled:opacity-60`}>
          {saving ? <><Loader size={12} className="animate-spin" />Guardando…</>
            : saved ? <><CheckCircle2 size={12} />¡Guardado!</>
            : <><Save size={12} />Guardar respuestas</>}
        </button>
        <button onClick={onContinue} disabled={!canContinue || busy}
          className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold px-5 py-2.5 rounded-xl shadow-sm">
          {busy
            ? <><Loader size={14} className="animate-spin" />La IA está cerrando el diagnóstico…</>
            : <><ArrowRight size={14} />Continuar</>}
        </button>
      </div>
      {!canContinue && currentQuestions.length > 0 && (
        <p className="text-xs text-amber-700 text-right">
          Responde las {currentQuestions.length} preguntas antes de continuar.
        </p>
      )}
    </>
  )
}

// ── Step: informe final renderizado ──────────────────────────────────────
function FinalReportView({ report, ficha }) {
  return (
    <div className="card border-2 border-indigo-200">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-indigo-100">
        <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
          <Sparkles size={15} className="text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900">Informe Final · Etapa 0</h3>
          <p className="text-xs text-gray-400">
            Generado el {new Date(ficha.etapa0_final_report_generated_at).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        </div>
      </div>
      <article className="prose prose-sm max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-relaxed prose-strong:text-gray-900 prose-li:text-gray-700">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{report}</ReactMarkdown>
      </article>
    </div>
  )
}
